import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Sparkles, UserPlus, Filter, Clock, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Appointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiRecommendation, setAiRecommendation] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterService, setFilterService] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const [apptsRes, empsRes] = await Promise.all([
        axios.get(`${API_URL}/api/appointments`),
        axios.get(`${API_URL}/api/employees`)
      ]);
      
      setAppointments(apptsRes.data);
      setEmployees(empsRes.data);
      setLoading(false);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching data:', error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getAIRecommendation = async (appointmentId) => {
    try {
      setShowAIModal(true);
      const res = await axios.post(`${API_URL}/api/ai/recommend`, {
        appointment_id: appointmentId
      });
      setAiRecommendation(res.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error getting AI recommendation:', error);
      alert('Error getting AI recommendation');
    }
  };

  const assignAppointment = async (appointmentId, employeeId, employeeName) => {
    try {
      await axios.put(`${API_URL}/api/appointments/${appointmentId}/assign`, {
        employee_id: employeeId,
        employee_name: employeeName
      });
      
      // Refresh appointments
      fetchData();
      setShowAssignModal(false);
      setShowAIModal(false);
      alert('Appointment assigned successfully!');
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error assigning appointment:', error);
      alert('Error assigning appointment');
    }
  };

  const updateStatus = async (appointmentId, status) => {
    try {
      await axios.put(`${API_URL}/api/appointments/${appointmentId}/status?status=${status}`);
      fetchData();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error updating status:', error);
    }
  };

  const getServiceIcon = (service) => {
    const icons = {
      nails: '💅',
      spray_tan: '☀️',
      facial: '✨',
      hair: '💇',
      makeup: '💄'
    };
    return icons[service] || '💎';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
      confirmed: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
      in_progress: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
      completed: 'bg-green-500/10 text-green-600 border-green-500/20',
      cancelled: 'bg-red-500/10 text-red-600 border-red-500/20'
    };
    return colors[status] || colors.pending;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      emergency: 'bg-red-500/10 text-red-600 border-red-500/20',
      high: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
      normal: 'bg-blue-500/10 text-blue-600 border-blue-500/20'
    };
    return colors[priority] || colors.normal;
  };

  const filteredAppointments = appointments.filter(apt => {
    if (filterStatus !== 'all' && apt.status !== filterStatus) return false;
    if (filterService !== 'all' && apt.service_type !== filterService) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Appointments</h1>
          <p className="text-muted-foreground mt-1">Manage bookings and assignments</p>
        </div>
        <Button className="bg-gradient-to-r from-amber-500 to-amber-600">
          <Calendar className="w-4 h-4 mr-2" />
          New Appointment
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Select value={filterService} onValueChange={setFilterService}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by service" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Services</SelectItem>
                <SelectItem value="nails">💅 Nails</SelectItem>
                <SelectItem value="spray_tan">☀️ Spray Tan</SelectItem>
                <SelectItem value="facial">✨ Facial</SelectItem>
                <SelectItem value="hair">💇 Hair</SelectItem>
                <SelectItem value="makeup">💄 Makeup</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Appointments List */}
      <div className="grid gap-4">
        {filteredAppointments.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium">No appointments found</p>
              <p className="text-muted-foreground">Change your filters or create a new appointment</p>
            </CardContent>
          </Card>
        ) : (
          filteredAppointments.map((apt) => (
            <Card key={apt.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="text-4xl">{getServiceIcon(apt.service_type)}</div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold">{apt.client_name}</h3>
                        <span className={`text-xs px-2 py-1 rounded-full border ${getPriorityColor(apt.priority)}`}>
                          {apt.priority}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full border ${getStatusColor(apt.status)}`}>
                          {apt.status.replace('_', ' ')}
                        </span>
                      </div>
                      
                      <p className="text-sm text-muted-foreground capitalize mb-3">
                        {apt.service_type.replace('_', ' ')} • {new Date(apt.appointment_date).toLocaleString()}
                      </p>
                      
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {apt.location}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {apt.estimated_duration} min
                        </div>
                        {apt.assigned_to_name && (
                          <div className="flex items-center gap-1">
                            <UserPlus className="w-4 h-4" />
                            {apt.assigned_to_name}
                          </div>
                        )}
                      </div>
                      
                      {apt.notes && (
                        <p className="text-sm mt-2 p-2 bg-accent/50 rounded">{apt.notes}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    {!apt.assigned_to && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedAppointment(apt);
                            getAIRecommendation(apt.id);
                          }}
                          className="whitespace-nowrap"
                        >
                          <Sparkles className="w-4 h-4 mr-1" />
                          AI Recommend
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedAppointment(apt);
                            setShowAssignModal(true);
                          }}
                        >
                          Assign
                        </Button>
                      </>
                    )}
                    
                    {apt.status === 'pending' && apt.assigned_to && (
                      <Button
                        size="sm"
                        onClick={() => updateStatus(apt.id, 'confirmed')}
                      >
                        Confirm
                      </Button>
                    )}
                    
                    {apt.status === 'confirmed' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateStatus(apt.id, 'in_progress')}
                      >
                        Start
                      </Button>
                    )}
                    
                    {apt.status === 'in_progress' && (
                      <Button
                        size="sm"
                        className="bg-green-500 hover:bg-green-600"
                        onClick={() => updateStatus(apt.id, 'completed')}
                      >
                        Complete
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Assign Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Appointment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Assign this {selectedAppointment?.service_type} appointment to:
            </p>
            <div className="space-y-2">
              {employees.map((emp) => (
                <button
                  key={emp.id}
                  onClick={() => assignAppointment(selectedAppointment.id, emp.id, emp.name)}
                  className="w-full p-3 text-left border rounded-lg hover:bg-accent transition-colors"
                >
                  <p className="font-medium">{emp.name}</p>
                  <p className="text-sm text-muted-foreground">
                    Skills: {emp.skills.join(', ')} • {emp.availability_status}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Recommendation Modal */}
      <Dialog open={showAIModal} onOpenChange={setShowAIModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              AI Recommendation
            </DialogTitle>
          </DialogHeader>
          {aiRecommendation && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-gradient-to-r from-amber-500/10 to-amber-600/10 border border-amber-500/20 rounded-lg">
                <h3 className="font-semibold text-lg mb-2">
                  Recommended: {aiRecommendation.recommended_employee_name}
                </h3>
                <p className="text-sm text-muted-foreground mb-3">{aiRecommendation.reasoning}</p>
                <div className="flex flex-wrap gap-2">
                  {aiRecommendation.factors.map((factor) => (
                    <span key={factor} className="text-xs px-2 py-1 bg-amber-500/20 text-amber-700 dark:text-amber-300 rounded-full">
                      {factor}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Confidence: {(aiRecommendation.confidence_score * 100).toFixed(0)}%
                </p>
              </div>

              <Button
                onClick={() => assignAppointment(
                  selectedAppointment.id,
                  aiRecommendation.recommended_employee_id,
                  aiRecommendation.recommended_employee_name
                )}
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600"
              >
                Assign to {aiRecommendation.recommended_employee_name}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                Or choose a different employee below:
              </p>

              <div className="space-y-2 max-h-60 overflow-y-auto">
                {employees
                  .filter(e => e.id !== aiRecommendation.recommended_employee_id)
                  .map((emp) => (
                    <button
                      key={emp.id}
                      onClick={() => assignAppointment(selectedAppointment.id, emp.id, emp.name)}
                      className="w-full p-3 text-left border rounded-lg hover:bg-accent transition-colors"
                    >
                      <p className="font-medium">{emp.name}</p>
                      <p className="text-sm text-muted-foreground">
                        Skills: {emp.skills.join(', ')}
                      </p>
                    </button>
                  ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Appointments;